import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import axios from "axios";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import ServiceItemDrawer from "../components/ServiceItemDrawer";
import EditServiceItemDrawer from "../components/EditServiceItemDrawer";
import { useDeleteServiceItem } from "../hooks";
import { ServiceItem } from "../types";

const TRANSLATION_KEY = "settings.product";

export const useServiceItemActions = (refetchProducts: () => void) => {
  const { t } = useTranslation();
  const deleteServiceItemMutation = useDeleteServiceItem();

  const [addProductId, setAddProductId] = useState<string | null>(null);
  const [editingServiceItem, setEditingServiceItem] =
    useState<ServiceItem | null>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [serviceItemToDelete, setServiceItemToDelete] =
    useState<ServiceItem | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);

  const openAddServiceItem = (productId: string) => setAddProductId(productId);

  const openEditServiceItem = (serviceItem: ServiceItem) => {
    setEditingServiceItem(serviceItem);
    setIsEditDrawerOpen(true);
  };

  const requestDeleteServiceItem = (serviceItem: ServiceItem) => {
    setServiceItemToDelete(serviceItem);
    setIsDeleteConfirmed(false);
    setIsDeleteModalOpen(true);
  };

  const closeEditDrawer = () => {
    setIsEditDrawerOpen(false);
    setEditingServiceItem(null);
  };

  // Keeps the service so the closing modal doesn't swap to the unbundled copy.
  const resetDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setIsDeleteConfirmed(false);
  };

  const handleConfirmDelete = async () => {
    if (!serviceItemToDelete) return;

    try {
      await deleteServiceItemMutation.mutateAsync({
        pathParams: {
          productId: serviceItemToDelete.productId,
          serviceItemId: serviceItemToDelete.id
        }
      });
    } catch (error) {
      // 409: active revenue items still reference the service item.
      const isInUse =
        axios.isAxiosError(error) && error.response?.status === 409;
      toast.error(
        t(
          `${TRANSLATION_KEY}.deleteServiceItemModal.${
            isInUse ? "inUseError" : "deleteError"
          }`
        )
      );
    } finally {
      refetchProducts();
      resetDeleteModal();
    }
  };

  const isDeletingBundledService = serviceItemToDelete?.inBundles === true;

  const dialogs = (
    <>
      {addProductId && (
        <ServiceItemDrawer
          isOpen
          productId={addProductId}
          serviceItem={null}
          onClose={() => setAddProductId(null)}
          onSave={() => {
            refetchProducts();
            setAddProductId(null);
          }}
          onRefetchProducts={refetchProducts}
        />
      )}
      {editingServiceItem && (
        <EditServiceItemDrawer
          key={editingServiceItem.id}
          isOpen={isEditDrawerOpen}
          productId={editingServiceItem.productId}
          serviceItem={editingServiceItem}
          isInBundles={editingServiceItem.inBundles === true}
          onClose={closeEditDrawer}
          onRefetchProducts={refetchProducts}
        />
      )}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title={t(`${TRANSLATION_KEY}.deleteServiceItemModal.title`)}
        confirmationText={
          isDeletingBundledService ? (
            t(`${TRANSLATION_KEY}.deleteServiceItemModal.descriptionInBundles`)
          ) : (
            <>
              {t(`${TRANSLATION_KEY}.deleteServiceItemModal.description`)}
              <br />
              {t(`${TRANSLATION_KEY}.deleteServiceItemModal.descriptionLine2`)}
            </>
          )
        }
        submitButtonText={
          isDeletingBundledService
            ? t(`${TRANSLATION_KEY}.deleteServiceItemModal.submitInBundles`)
            : undefined
        }
        handleSubmit={handleConfirmDelete}
        handleCancel={resetDeleteModal}
        checkboxLabel={t(
          `${TRANSLATION_KEY}.deleteServiceItemModal.checkboxLabel`
        )}
        isCheckboxRequired={true}
        onCheckboxChange={setIsDeleteConfirmed}
        isCheckboxChecked={isDeleteConfirmed}
        data-test="products-delete-service-modal"
      />
    </>
  );

  return {
    openAddServiceItem,
    openEditServiceItem,
    requestDeleteServiceItem,
    dialogs
  };
};
